import React from "react";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import api, { formatError, formatRupiah } from "@/lib/api";
import { Plus, Pencil, Trash2, ArrowLeft, Store, Upload } from "lucide-react";

const CLOUD_NAME = "dcstha9qi";
const UPLOAD_PRESET = "ml_default";

const empty = { name: "", description: "", price: 0, cost: 0, stock: 0, type: "digital", category_id: "", image_url: "", sku: "" };

export default function SellerProducts() {
  const [items, setItems] = useState([]);
  const [cats, setCats] = useState([]);
  const [seller, setSeller] = useState(undefined);
  const [form, setForm] = useState(empty);
  const [editing, setEditing] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [uploading, setUploading] = useState(false);

  const load = () => {
    api.get("/seller/me").then((r) => setSeller(r.data));
    api.get("/seller/products").then((r) => setItems(r.data));
    api.get("/categories").then((r) => setCats(r.data));
  };
  useEffect(load, []);

  const uploadImage = async (file) => {
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("upload_preset", UPLOAD_PRESET);
      const res = await fetch(`https://api.cloudinary.com/v1_1/${CLOUD_NAME}/image/upload`, {
        method: "POST",
        body: formData,
      });
      const data = await res.json();
      if (data.secure_url) {
        setForm((prev) => ({ ...prev, image_url: data.secure_url }));
        toast.success("Gambar berhasil diupload!");
      } else {
        toast.error("Gagal upload gambar");
      }
    } catch (err) {
      toast.error("Gagal upload gambar");
    } finally {
      setUploading(false);
    }
  };

  if (seller === undefined) return <div className="p-12 font-mono text-xs">// LOADING</div>;
  if (seller === null) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-16 text-center" data-testid="not-seller">
        <Store className="w-12 h-12 mx-auto" />
        <h1 className="font-display text-3xl font-black uppercase mt-4">Belum Jadi Penjual</h1>
        <p className="text-zinc-600 mt-2">Aktifkan mode penjual dulu di halaman profil.</p>
        <Link to="/profile" className="btn-primary inline-block mt-6">Buka Profil →</Link>
      </div>
    );
  }

  const save = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...form, price: Number(form.price), cost: Number(form.cost || 0), stock: Number(form.stock) };
      if (editing) await api.put(`/seller/products/${editing}`, payload);
      else await api.post("/seller/products", payload);
      toast.success("Produk disimpan");
      setForm(empty); setEditing(null); setShowForm(false); load();
    } catch (err) { toast.error(formatError(err)); }
  };

  const edit = (p) => { setForm(p); setEditing(p.id); setShowForm(true); };
  const remove = async (p) => {
    if (!window.confirm(`Hapus ${p.name}?`)) return;
    await api.delete(`/seller/products/${p.id}`); toast.success("Dihapus"); load();
  };

  return (
    <div className="max-w-6xl mx-auto px-4 md:px-8 py-10" data-testid="seller-products">
      <Link to="/profile" className="label-mono text-zinc-500 hover:text-black flex items-center gap-1">
        <ArrowLeft className="w-3 h-3" /> Kembali ke Profil
      </Link>

      <div className="mt-4 flex flex-wrap justify-between items-center gap-3">
        <div>
          <div className="label-mono text-zinc-500">// {seller.shop_name?.toUpperCase()}</div>
          <h1 className="font-display text-3xl md:text-4xl font-black uppercase mt-1">Produk Saya</h1>
        </div>
        <button onClick={() => { setEditing(null); setForm(empty); setShowForm(!showForm); }} className="btn-primary inline-flex items-center gap-2" data-testid="add-product-btn">
          <Plus className="w-4 h-4" /> Upload Produk
        </button>
      </div>

      {showForm && (
        <form onSubmit={save} className="mt-6 border border-black bg-white p-6 grid md:grid-cols-2 gap-4 shadow-brutal">
          <div>
            <label className="label-mono block mb-2">Nama Produk</label>
            <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="input-brutal" required />
          </div>
          <div>
            <label className="label-mono block mb-2">SKU (opsional)</label>
            <input value={form.sku} onChange={(e) => setForm({ ...form, sku: e.target.value })} className="input-brutal" />
          </div>
          <div>
            <label className="label-mono block mb-2">Tipe</label>
            <select value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} className="input-brutal">
              <option value="digital">Digital</option>
              <option value="physical">Fisik</option>
            </select>
          </div>
          <div>
            <label className="label-mono block mb-2">Kategori</label>
            <select value={form.category_id} onChange={(e) => setForm({ ...form, category_id: e.target.value })} className="input-brutal" required>
              <option value="">-- pilih --</option>
              {cats.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>
          <div>
            <label className="label-mono block mb-2">Harga Jual ($)</label>
            <input type="number" step="0.01" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} className="input-brutal" required />
          </div>
          <div>
            <label className="label-mono block mb-2">HPP / Modal ($)</label>
            <input type="number" step="0.01" value={form.cost} onChange={(e) => setForm({ ...form, cost: e.target.value })} className="input-brutal" placeholder="Komisi referral 10% dari profit" />
          </div>
          <div>
            <label className="label-mono block mb-2">Stok</label>
            <input type="number" value={form.stock} onChange={(e) => setForm({ ...form, stock: e.target.value })} className="input-brutal" />
          </div>
          <div>
            <label className="label-mono block mb-2">Gambar Produk</label>
            <div className="border border-black p-3 bg-zinc-50">
              <input
                type="file"
                accept="image/*"
                onChange={(e) => { if (e.target.files[0]) uploadImage(e.target.files[0]); }}
                className="hidden"
                id="img-upload"
              />
              <label htmlFor="img-upload" className="cursor-pointer flex items-center gap-2 font-mono text-xs font-bold hover:text-zinc-600">
                <Upload className="w-4 h-4" />
                {uploading ? "// UPLOADING..." : "Pilih Gambar"}
              </label>
              {form.image_url && (
                <div className="mt-2">
                  <img src={form.image_url} alt="preview" className="w-24 h-24 object-cover border border-black" />
                  <div className="text-[10px] font-mono text-zinc-500 mt-1 break-all">{form.image_url}</div>
                </div>
              )}
            </div>
          </div>
          <div className="md:col-span-2">
            <label className="label-mono block mb-2">Deskripsi</label>
            <textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={3} className="input-brutal" required />
          </div>
          <div className="md:col-span-2 flex gap-3">
            <button type="submit" disabled={uploading} className="btn-primary" data-testid="save-product">
              {editing ? "Update" : "Simpan & Upload"}
            </button>
            <button type="button" onClick={() => { setShowForm(false); setEditing(null); setForm(empty); }} className="btn-secondary">Batal</button>
          </div>
        </form>
      )}

      <div className="mt-6 border border-black bg-white overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-zinc-50 border-b border-black">
            <tr>
              <th className="p-3 text-left label-mono">PRODUK</th>
              <th className="p-3 text-left label-mono hidden md:table-cell">TIPE</th>
              <th className="p-3 text-right label-mono">HARGA</th>
              <th className="p-3 text-right label-mono">STOK</th>
              <th className="p-3 text-right label-mono">TERJUAL</th>
              <th className="p-3 text-right label-mono">AKSI</th>
            </tr>
          </thead>
          <tbody>
            {items.map((p) => (
              <tr key={p.id} className="border-b border-zinc-200" data-testid={`my-prod-${p.id}`}>
                <td className="p-3 flex items-center gap-3">
                  {p.image_url
                    ? <img src={p.image_url} alt="" className="w-10 h-10 object-cover border border-black" />
                    : <div className="w-10 h-10 border border-black bg-zinc-100 flex items-center justify-center font-mono text-[8px] text-zinc-400">NO IMG</div>
                  }
                  <div>
                    <div className="font-mono text-xs font-bold">{p.name}</div>
                    <div className="text-[10px] text-zinc-500 font-mono">{p.sku}</div>
                  </div>
                </td>
                <td className="p-3 hidden md:table-cell"><span className="badge-status bg-white">{p.type.toUpperCase()}</span></td>
                <td className="p-3 text-right font-mono text-xs">{formatRupiah(p.price)}</td>
                <td className="p-3 text-right font-mono text-xs">{p.stock}</td>
                <td className="p-3 text-right font-mono text-xs">{p.sold_count || 0}</td>
                <td className="p-3 text-right">
                  <button onClick={() => edit(p)} className="p-2 border border-black hover:bg-black hover:text-white mr-2"><Pencil className="w-3 h-3" /></button>
                  <button onClick={() => remove(p)} className="p-2 border border-black hover:bg-[#FF3333] hover:text-white"><Trash2 className="w-3 h-3" /></button>
                </td>
              </tr>
            ))}
            {items.length === 0 && <tr><td colSpan={6} className="p-12 text-center font-mono text-xs text-zinc-500">// Belum ada produk · klik "Upload Produk" untuk mulai</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}